<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { format } from 'svelte-i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { getDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { Button, Text, Toggle, Input, Select } from '@Shelly/shelly-design-system';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;

	const id = real_id(device.id);
	const settings = readDeviceSettings(id);
	const motionConfig = settings?.['motion:0'] ?? {};
	const BLINDTIME_MIN = 0;
	const BLINDTIME_MAX = 1800;
	const SENSITIVITY_OPTIONS = [
		{ title: $format('option_lowest_sensitivity', { default: 'Lowest' }), value: 'lowest' },
		{ title: $format('option_low_sensitivity', { default: 'Low' }), value: 'low' },
		{ title: $format('option_normal_sensitivity', { default: 'Normal' }), value: 'normal' },
		{ title: $format('option_high_sensitivity', { default: 'High' }), value: 'high' },
	];

	let enable: boolean = motionConfig?.enable ?? true;
	let sensitivity: string = motionConfig?.sensitivity ?? 'normal';
	let blindtime: number = motionConfig?.blindtime ?? 2;

	let cacheEnable = enable;
	let cacheSensitivity = sensitivity;
	let cacheBlindtime = blindtime;
	let loading = false;

	const findSensitivity = (val: string) =>
		SENSITIVITY_OPTIONS.find((o) => o.value === val) ?? SENSITIVITY_OPTIONS[2];

	$: sensitivityValue = findSensitivity(sensitivity);

	$: blindtimeError = valueOutOfRange(blindtime, BLINDTIME_MIN, BLINDTIME_MAX)
		? $format('min_max_error_description', {
				values: { min: BLINDTIME_MIN, max: BLINDTIME_MAX },
				default: 'Value must be between {min} and {max}.',
			})
		: '';

	$: hasChanges =
		cacheEnable !== enable || cacheSensitivity !== sensitivity || cacheBlindtime !== blindtime;

	const save = async () => {
		if (!hasChanges || blindtimeError) return;

		loading = true;
		const blindtimeNum = Number(blindtime);
		const params = {
			config: { enable, sensitivity, blindtime: blindtimeNum },
			id: device.channel,
		};
		const res: SettingRequest = await gen2Request(device, 'Motion.SetConfig', params);
		const response = onSettingsResponse(res, device);

		if (response) {
			settings['motion:0'] = { ...motionConfig, enable, sensitivity, blindtime: blindtimeNum };
			devices_settings.update_devices_settings(id, settings);
			cacheEnable = enable;
			cacheSensitivity = sensitivity;
			cacheBlindtime = blindtime;
		} else {
			enable = cacheEnable;
			sensitivity = cacheSensitivity;
			blindtime = cacheBlindtime;
		}
		loading = false;
	};
</script>

<div class="d-flex align-items-center justify-content-between mb-m">
	<Text styleType="body-small">
		{$format('motion_enable_description', { default: 'Enable motion detection.' })}
	</Text>
	<Toggle
		bind:checked={enable}
		id="toggle-motion-enable"
		aria-label={$format('motion_enable_description', { default: 'Enable motion detection.' })}
	/>
</div>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_sensitivity_label', { default: 'Sensitivity' })}
</Text>
<Select
	class="mb-m"
	options={SENSITIVITY_OPTIONS}
	value={sensitivityValue}
	on:change={(e) => (sensitivity = e.detail.value)}
	id="select-motion-sensitivity"
	aria-label={$format('motion_sensitivity_label', { default: 'Sensitivity' })}
/>

<Text styleType="label-small" class="mb-xs">
	{$format('motion_blindtime_label', { default: 'Blindtime (seconds)' })}
</Text>
<Input
	class="mb-m"
	size="m"
	bind:value={blindtime}
	type="number"
	min={BLINDTIME_MIN}
	max={BLINDTIME_MAX}
	error={blindtimeError}
	icon="fas fa-eye-slash"
	id="input-motion-blindtime"
	aria-labelledby="input-motion-blindtime"
/>

<Button size="m" width="full" disabled={!hasChanges || !!blindtimeError} {loading} on:click={save}>
	{$format('button_save', { default: 'Save' })}
</Button>
